import _, { isNaN } from 'lodash';
import React, { useMemo } from 'react';
import Select from 'react-select';
import { gql, useMutation, useQuery } from '@apollo/client';
import { AGE_GROUP_FIELDS } from '../../../lib/queries/age-group.queries';
import { POSITION_FIELDS } from '../../../lib/queries/position.queries';
import { GET_SESSION_ATTENDANCE_RESPONSES } from '../../../lib/queries/attendance-response.queries';

// Material UI
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';

// Data
import { getAuthUser } from '../../../lib/services/auth.service';
import { useActiveAgeGroupIdsInAssessment } from '../../../lib/services/age-group.service';
import { useLogQueryError } from '../../../lib/hooks/use-log-query-error';
import { useRetryKey } from '../../../lib/hooks/use-retry-key';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { getMessages } from './assessment-session-player-list.messages';

// Components
import { RsvpBadge } from '../../Attendance/rsvp-badge.component';
import { ContactReachIcon } from '../../PlayerContact/contact-reach-icon.component';
import { SessionPlayerNotificationToggle } from '../SessionPlayerNotificationToggle/session-player-notification-toggle.component';
import { buildRsvpLookup, buildRsvpResponseLookup, getRsvpReasonText } from '../../Attendance/attendance.helpers';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { getPlayerName } from '../../../lib/utils/player-name.service';

const GET_ASSESSMENT_SESSION_PLAYER_ASSESSMENT_LIST = gql`
  query GetAssessmentSessionPlayerAssessmentList($_id: ID!, $assessmentId: ID!, $ageGroupIds: [ID!]!) {
    assessmentSession(_id: $_id) {
      _id
      isAttendanceRequested
      isPlayerNotificationEnabled
      playerAssessments {
        _id
        player {
          _id
          firstName
          lastName
          nickname
        }
        position {
          ...PositionFields
        }
        ageGroup {
          ...AgeGroupFields
        }
        contactReach
        deletedAt
      }
    }
    playerAssessments(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds) {
      _id
      player {
        _id
        firstName
        lastName
        nickname
      }
      position {
        ...PositionFields
      }
      ageGroup {
        ...AgeGroupFields
      }
      contactReach
      deletedAt
    }
  }

  ${AGE_GROUP_FIELDS}
  ${POSITION_FIELDS}
`;

export const UPDATE_ASSESSMENT_SESSION_PLAYER_ASSESSMENTS = gql`
  mutation UpdateAssessmentSessionPlayerAssessments($_id: ID!, $data: UpdateAssessmentSessionInput!, $notify: Boolean, $notifyPlayerAssessmentIds: [ID!], $operationId: String) {
    updateAssessmentSession(_id: $_id, data: $data, notify: $notify, notifyPlayerAssessmentIds: $notifyPlayerAssessmentIds, operationId: $operationId) {
      _id
      playerAssessments {
        _id
        player {
          _id
          firstName
          lastName
          nickname
        }
        contactReach
      }
      checkedInPlayerIds
    }
  }
`;

interface IAssessmentSessionPlayerAssessmentList {
  assessmentSessionId: string;
}

export const AssessmentSessionPlayerAssessmentList: React.FC<IAssessmentSessionPlayerAssessmentList> = ({ assessmentSessionId }) => {
  const termLookup = useCompanySportTerminology()
  const messages = useMemo(() => getMessages(termLookup), [termLookup]);

  const user = getAuthUser();
  const { ageGroupIds, error: ageGroupsError } = useActiveAgeGroupIdsInAssessment();
  const { data, error: rosterError } = useQuery(GET_ASSESSMENT_SESSION_PLAYER_ASSESSMENT_LIST, {
    skip: !user || !ageGroupIds,
    variables: {
      _id: assessmentSessionId,
      assessmentId: user?.settings.activeAssessmentId,
      ageGroupIds
    },
    fetchPolicy: 'cache-and-network'
  });
  useLogQueryError(rosterError, 'AssessmentSessionPlayerAssessmentList');
  // Without this a failed load renders as an empty roster, indistinguishable from "None".
  const loadFailed = Boolean(ageGroupsError || rosterError);

  const playerAssessments: IPlayerAssessment[] = useMemo(() => (data && data.playerAssessments) || [], [data]);
  const selectedPlayerAssessments = _.get(data, 'assessmentSession.playerAssessments', []);
  const isAttendanceRequested: boolean = Boolean(_.get(data, 'assessmentSession.isAttendanceRequested'));
  const sendNotification: boolean = _.get(data, 'assessmentSession.isPlayerNotificationEnabled') === true;

  const { data: rsvpData } = useQuery(GET_SESSION_ATTENDANCE_RESPONSES, {
    variables: { _id: assessmentSessionId },
    skip: !isAttendanceRequested,
    fetchPolicy: 'cache-and-network',
  });
  const attendanceResponses: IAttendanceResponse[] = _.get(rsvpData, 'assessmentSession.attendanceResponses', []);
  // Apollo returns a stable array reference on cache hits, so this memo refreshes
  // on real cache updates and stays stable across unrelated re-renders.
  const getRsvpStatus = useMemo(() => buildRsvpLookup(attendanceResponses), [attendanceResponses]);
  const getRsvpResponse = useMemo(() => buildRsvpResponseLookup(attendanceResponses), [attendanceResponses]);

  const availableHash = useMemo(() => _.keyBy(playerAssessments, '_id'), [playerAssessments]);
  const addOptions = useMemo(() => {
    const selectedIds = new Set(_.map(selectedPlayerAssessments, '_id'));
    return playerAssessments
      .filter((pa) => !selectedIds.has(pa._id))
      .map((pa) => ({ value: pa._id, label: `${getPlayerName(pa)} - ${pa.ageGroup.name} - ${pa.position.name}` }));
  }, [playerAssessments, selectedPlayerAssessments]);

  // Serialized so two quick roster edits can't reach the server out of order. The roster only
  // advances in `update`, so the controls also stay disabled until each edit's response lands.
  const [updateAssessmentSession, { loading: rosterSaving }] = useMutation(UPDATE_ASSESSMENT_SESSION_PLAYER_ASSESSMENTS, {
    context: { serializationKey: 'MUTATION' },
  });
  // A retry of the same roster edit reuses its operationId, so its notice goes out once.
  const withRetryKey = useRetryKey();

  const confirmNotify = ({player}: IPlayerAssessment) =>
    sendNotification && window.confirm(`Are you sure you want to send notification to ${getPlayerName({player, firstNameFirst: true})}?`)

  const handleAddPlayerAssessment = (playerAssessment: IPlayerAssessment) => {
    const nextPlayerAssessmentIds = [..._.map(selectedPlayerAssessments, '_id'), playerAssessment._id];

    const notify = confirmNotify(playerAssessment)
    const variables = {
      _id: assessmentSessionId,
      data: { playerAssessmentIds: nextPlayerAssessmentIds },
      notify,
      notifyPlayerAssessmentIds: [playerAssessment._id]
    };
    return withRetryKey(variables, (operationId) => updateAssessmentSession({
      variables: { ...variables, operationId },
      update: (cache) => {
        cache.modify({
          id: `AssessmentSession:${assessmentSessionId}`,
          fields: {
            playerAssessments: () => [...selectedPlayerAssessments, playerAssessment],
            playerCount: (prev) => isNaN(prev) ? undefined : prev + 1
          }
        });

        cache.modify({
          id: `PlayerAssessment:${playerAssessment._id}`,
          fields: {assessmentSessionCount: (prev) => isNaN(prev) ? undefined : prev + 1}
        })
      }
    }))
  };

  const handleRemovePlayerAssessment = (playerAssessment: IPlayerAssessment) => {
    if (window.confirm(`Are you sure you want to remove ${getPlayerName({player: playerAssessment.player, firstNameFirst: true})} from this ${termLookup[SportTerminology.SESSION].toLowerCase()}?`)) {
      const nextPlayerAssessments = [...selectedPlayerAssessments];
      _.remove(nextPlayerAssessments, currPlayerAssessment => currPlayerAssessment._id === playerAssessment._id);
      const nextPlayerAssessmentIds = _.map(nextPlayerAssessments, '_id');

      const notify = confirmNotify(playerAssessment)
      const variables = {
        _id: assessmentSessionId,
        data: {playerAssessmentIds: nextPlayerAssessmentIds},
        notify,
        notifyPlayerAssessmentIds: [playerAssessment._id]
      };
      return withRetryKey(variables, (operationId) => updateAssessmentSession({
        variables: { ...variables, operationId },
        update: (cache) => {
          cache.modify({
            id: `AssessmentSession:${assessmentSessionId}`,
            fields: {
              playerAssessments: () => nextPlayerAssessments,
              playerCount: (prev) => isNaN(prev) ? undefined : prev - 1
            }
          });

          cache.modify({
            id: `PlayerAssessment:${playerAssessment._id}`,
            fields: {assessmentSessionCount: (prev) => isNaN(prev) ? undefined : prev - 1}
          })
        }
      }));
    }
  };

  return (
    <div>
      {data?.assessmentSession && (
        <Box mb={2}>
          <SessionPlayerNotificationToggle
            assessmentSessionId={assessmentSessionId}
            isPlayerNotificationEnabled={sendNotification}
          />
        </Box>
      )}
      <Box mb={2}>
        <Select
          placeholder={`Add ${termLookup[SportTerminology.ATHLETE].toLowerCase()}...`}
          options={addOptions}
          value={null}
          isDisabled={rosterSaving}
          onChange={(option: any) => option && handleAddPlayerAssessment(availableHash[option.value])}
        />
      </Box>
      {loadFailed && !data ? (
        <Typography variant="body2" color="error"><IntlMsg {...messages.errRosterNotLoaded} /></Typography>
      ) : selectedPlayerAssessments.length === 0 ? (
        <Typography variant="body2" color="textSecondary"><IntlMsg {...messages.emptyRoster} /></Typography>
      ) : (
        <Table size="small" style={{ border: '1px solid #ddd' }}>
          <TableHead>
            <TableRow>
              <TableCell>{termLookup[SportTerminology.ATHLETE]}</TableCell>
              {isAttendanceRequested && (
                <TableCell>
                  <Typography variant="subtitle2" color="textSecondary">RSVP</Typography>
                </TableCell>
              )}
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedPlayerAssessments.map((playerAssessment: IPlayerAssessment) => (
              <TableRow key={playerAssessment._id}>
                <TableCell>
                  <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
                    <Typography variant="body2">{getPlayerName(playerAssessment)}</Typography>
                    <ContactReachIcon contactReach={playerAssessment.contactReach} />
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {playerAssessment.ageGroup.name}, {playerAssessment.position.name}
                  </Typography>
                </TableCell>
                {isAttendanceRequested && (
                  <TableCell>
                    <RsvpBadge status={getRsvpStatus(playerAssessment._id)} />
                    {getRsvpReasonText(getRsvpResponse(playerAssessment._id)) && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {getRsvpReasonText(getRsvpResponse(playerAssessment._id))}
                      </Typography>
                    )}
                  </TableCell>
                )}
                <TableCell padding="checkbox" align="right">
                  <IconButton aria-label="delete" disabled={rosterSaving} onClick={() => handleRemovePlayerAssessment(playerAssessment)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

